import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import Modal from '../../components/Modal';
import {
  createNotification,
  deleteNotification,
  listNotifications,
  resendNotification,
  updateNotification,
} from '../../api/notifications';
import { listUsers } from '../../api/users';

export default function NotificationsSection({ token }) {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editError, setEditError] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [busyNotificationId, setBusyNotificationId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [notificationsData, usersData] = await Promise.all([
        listNotifications(token),
        listUsers(token),
      ]);
      setNotifications(notificationsData);
      setUsers(usersData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const senderName = (userId) => users.find((u) => u.user_id === userId)?.name || `#${userId}`;

  const recipientsLabel = (notification) => {
    if (!notification.user_ids || notification.user_ids.length === 0) return 'All users';
    const names = notification.user_ids.map(
      (id) => users.find((u) => u.user_id === id)?.name || `#${id}`
    );
    return names.join(', ');
  };

  const toggleUserId = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const openEdit = (notification) => {
    setEditing(notification);
    setEditTitle(notification.title);
    setEditBody(notification.body);
    setEditError('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => setEditModalOpen(false);

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setIsSavingEdit(true);
    setEditError('');
    try {
      await updateNotification(token, editing.notification_id, {
        title: editTitle,
        body: editBody,
      });
      closeEditModal();
      await load();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (notification) => {
    if (!window.confirm(`Delete notification "${notification.title}"?`)) return;
    setBusyNotificationId(notification.notification_id);
    setError('');
    try {
      await deleteNotification(token, notification.notification_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyNotificationId(null);
    }
  };

  const handleResend = async (notification) => {
    if (
      !window.confirm(
        `Resend notification "${notification.title}" to ${recipientsLabel(notification)}?`
      )
    )
      return;
    setBusyNotificationId(notification.notification_id);
    setError('');
    try {
      await resendNotification(token, notification.notification_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyNotificationId(null);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!sendToAll && selectedUserIds.length === 0) {
      setSendError('Select at least one user, or choose "All users".');
      return;
    }
    setIsSending(true);
    setSendError('');
    setSendSuccess('');
    try {
      await createNotification(token, {
        title,
        body,
        user_ids: sendToAll ? null : selectedUserIds,
      });
      setTitle('');
      setBody('');
      setSendToAll(true);
      setSelectedUserIds([]);
      setSendSuccess('Notification sent');
      await load();
    } catch (err) {
      setSendError(err.message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Notifications</h2>

      <form
        onSubmit={handleSend}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            required
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
          <div className="flex items-center gap-4 mb-2">
            <label className="flex items-center gap-1.5 text-sm text-gray-700">
              <input
                type="radio"
                checked={sendToAll}
                onChange={() => setSendToAll(true)}
              />
              All users
            </label>
            <label className="flex items-center gap-1.5 text-sm text-gray-700">
              <input
                type="radio"
                checked={!sendToAll}
                onChange={() => setSendToAll(false)}
              />
              Specific users
            </label>
          </div>

          {!sendToAll && (
            <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-100">
              {users.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-400">No users available</p>
              ) : (
                users.map((u) => (
                  <label
                    key={u.user_id}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.user_id)}
                      onChange={() => toggleUserId(u.user_id)}
                    />
                    {u.name} <span className="text-gray-400">({u.mobile})</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {sendError && <p className="text-sm text-red-600">{sendError}</p>}
        {sendSuccess && <p className="text-sm text-indigo-600">{sendSuccess}</p>}

        <button
          type="submit"
          disabled={isSending}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSending ? 'Sending...' : 'Send notification'}
        </button>
      </form>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Title</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Message</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Recipients</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Sent by</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Sent at</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : notifications.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  No notifications sent yet
                </td>
              </tr>
            ) : (
              notifications.map((n) => {
                const isBusy = busyNotificationId === n.notification_id;
                return (
                  <tr key={n.notification_id}>
                    <td className="px-4 py-2 text-gray-900">{n.title}</td>
                    <td className="px-4 py-2 text-gray-600 max-w-md truncate">{n.body}</td>
                    <td className="px-4 py-2 text-gray-600 max-w-xs truncate" title={recipientsLabel(n)}>
                      {recipientsLabel(n)}
                    </td>
                    <td className="px-4 py-2 text-gray-600">{senderName(n.sent_by)}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(n.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                      <button
                        onClick={() => handleResend(n)}
                        disabled={isBusy}
                        className="text-indigo-600 hover:underline disabled:opacity-50"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => openEdit(n)}
                        disabled={isBusy}
                        className="text-gray-600 hover:underline disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(n)}
                        disabled={isBusy}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {editModalOpen && (
        <Modal title="Edit notification" onClose={closeEditModal}>
          <form onSubmit={handleSaveEdit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                required
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                required
                rows={3}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {editError && <p className="text-sm text-red-600">{editError}</p>}
            <button
              type="submit"
              disabled={isSavingEdit}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSavingEdit ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </section>
  );
}
